import React, { useEffect, useState, useRef } from 'react'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, useHistory, Link } from 'react-router-dom'
import { Spin } from 'antd'
import blueLogo from '@assets/blue-logo.png'
import { fetchShellUserByToken } from '@reducers/shellUser/shellUserSlice'
import {
  selectShellUser,
  selectShellUserCompanyName,
  selectShellUserInvitee,
  selectShellUserInvitationReason,
  selectShellUserStatus,
  selectHasShellUserJwt,
} from '@reducers/shellUser/shellUserSelectors'
import { selectCurrentUser, selectCurrentUserStatus } from '@reducers/currentUser/currentUserSelectors'
import { SUCCEEDED, ERROR } from '@constants/fetchStatuses'
import {
  CREATE_ACCOUNT,
  DEFAULT_ROUTE,
  INFO_STEP,
  LOGIN,
  LOGOUT,
  ACCOUNT_SETUP,
  SIGNUP,
  SIGNUP_SUCCESS,
} from '@constants/routes'
import {
  IN_PROGRESS,
  COMPLETE,
  COMPANY,
  CONTRACT_APPRAISER,
  PROPERTY_DATA_COLLECTOR,
} from '@components/Signup/constants'
import { getCurrentUser } from '@actions/currentUser'
import CreateAccountContainer from '@components/Signup/SignupWizard/steps/CreateAccount/CreateAccountContainer'
import Info from '@components/Signup/SignupWizard/steps/Info/Info'
import SignupWizard from './SignupWizard/SignupWizard'
import SignupSuccess from './SignupSuccess'

import '@components/Signup/NewSignup.scss'

const NewSignup = ({ location: { search, pathname } = {} }) => {
  const { t: token = '' } = queryString.parse(search)
  const dispatch = useDispatch()
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [expiredInvite, setExpiredInvite] = useState(false)
  const [invalidInvite, setInvalidInvite] = useState(false)
  const navigationHandledRef = useRef(false)

  const currentUser = useSelector(selectCurrentUser)
  const hasCurrentUser = Object.keys(currentUser).length > 0

  const { user_type: currentUserType, is_appraiser: isAppraiser = true, appraiser_type: appraiserType } = currentUser

  const shellUser = useSelector(selectShellUser) || {}
  const shellUserEmail = shellUser.email
  const firstname = shellUser.firstname
  const lastname = shellUser.lastname
  const shellUserType = shellUser.userType
  const isShell = shellUser.is_shell

  const invitee = useSelector(selectShellUserInvitee)
  const invitationReason = useSelector(selectShellUserInvitationReason)
  const shellUserStatus = useSelector(selectShellUserStatus)
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const companyName = useSelector(selectShellUserCompanyName)
  const hasShellUserJwt = useSelector(selectHasShellUserJwt)
  const isPdcUser = shellUserType === PROPERTY_DATA_COLLECTOR
  const initialValues = {
    email: shellUserEmail,
    firstname,
    lastname,
    company_name: companyName,
  }

  const userType = shellUserType || currentUserType || COMPANY

  const userInfo = {
    isAppraiser,
    appraiserType: appraiserType || CONTRACT_APPRAISER,
  }

  useEffect(() => {
    // Skip API calls if we're on the success page - user is already registered
    if (pathname === SIGNUP_SUCCESS) {
      return
    }

    dispatch(getCurrentUser())
    dispatch(fetchShellUserByToken(token))
  }, [dispatch, history, token, pathname])

  useEffect(() => {
    const currentUserCompleted = currentUserStatus === SUCCEEDED || currentUserStatus === ERROR
    const shellUserCompleted = shellUserStatus === SUCCEEDED || shellUserStatus === ERROR
    const bothCallsCompleted = currentUserCompleted && shellUserCompleted
    // No currentUser, shellUser exists and is is_shell true
    const validShellUser = bothCallsCompleted && !hasCurrentUser && shellUserEmail && isShell
    // Shell user needs to complete signup (has JWT from shell user token)
    const continueShellUserSignup = bothCallsCompleted && hasShellUserJwt
    // No currentUser, shellUser exists but has already created an account
    const expiredInvite = bothCallsCompleted && !hasCurrentUser && shellUserEmail && !isShell
    // Invite does not match email of logged in user
    const invalidInvite = bothCallsCompleted && hasCurrentUser && shellUserEmail && currentUser.email !== shellUserEmail

    // User is already logged in
    const loggedInComplete =
      bothCallsCompleted &&
      hasCurrentUser &&
      shellUserEmail &&
      !isShell &&
      currentUser.signup_progress === COMPLETE &&
      currentUser.email === shellUserEmail

    // In progress
    const inProgress = bothCallsCompleted && hasCurrentUser && currentUser.signup_progress === IN_PROGRESS

    const noUsers = bothCallsCompleted && !hasCurrentUser && !shellUserEmail

    if (validShellUser) {
      if (pathname !== CREATE_ACCOUNT && !navigationHandledRef.current) {
        navigationHandledRef.current = true
        history.push({
          pathname: CREATE_ACCOUNT,
          search: `?t=${token}`,
        })
      }
      setLoading(false)
    } else if (continueShellUserSignup) {
      if (pathname !== CREATE_ACCOUNT && !navigationHandledRef.current) {
        navigationHandledRef.current = true
        history.push({
          pathname: CREATE_ACCOUNT,
          search: `?t=${token}`,
        })
      }
      setLoading(false)
    } else if (inProgress) {
      setLoading(false)
      if (pathname === SIGNUP && !navigationHandledRef.current) {
        navigationHandledRef.current = true
        history.push({ pathname: INFO_STEP })
      }
    } else if (loggedInComplete) {
      if (!navigationHandledRef.current) {
        navigationHandledRef.current = true
        history.push({ pathname: `/${DEFAULT_ROUTE}` })
      }
    } else if (expiredInvite || noUsers) {
      setExpiredInvite(true)
      setLoading(false)
    } else if (invalidInvite) {
      setInvalidInvite(true)
      setLoading(false)
    }
  }, [
    loading,
    setLoading,
    shellUserStatus,
    hasCurrentUser,
    currentUserStatus,
    currentUser,
    shellUserEmail,
    isShell,
    history,
    token,
    pathname,
    navigationHandledRef,
    hasShellUserJwt,
  ])

  const goToInfo = () =>
    history.push({
      pathname: INFO_STEP,
      search: `?t=${token}`,
    })

  const goToWizard = () =>
    history.push({
      pathname: ACCOUNT_SETUP,
      search: `?t=${token}`,
    })

  return (
    <div className="new-signup">
      <div className="logo-bar">
        <img src={blueLogo} alt={blueLogo} className="signup-logo" />
        {hasCurrentUser && <Link to={LOGOUT}>Log Out</Link>}
      </div>
      <div className="signup-container">
        {loading
          ? (
          <Spin tip="Loading..." size="large" className="full-page-spinner" />
            )
          : (
          <React.Fragment>
            {expiredInvite
              ? (
              <div className="content">
                <div className="expired">
                  <span>Invite is no longer valid, please&nbsp;</span>
                  <span>
                    <Link to={LOGIN}>log in</Link>
                  </span>
                  <span>&nbsp;or request a new invite.</span>
                </div>
              </div>
                )
              : invalidInvite
                ? (
              <div className="content">
                <div className="expired">
                  <span>Please&nbsp;</span>
                  <span>
                    <Link to={LOGOUT}>log out</Link>
                  </span>
                  <span>&nbsp;before accepting a new invitation.</span>
                </div>
              </div>
                  )
                : (
              <Switch>
                <Route path={SIGNUP_SUCCESS}>
                  <SignupSuccess />
                </Route>
                <Route path={CREATE_ACCOUNT}>
                  <div className={isPdcUser ? 'pdc-content' : 'content'}>
                    <CreateAccountContainer
                      userType={userType}
                      handleContinue={goToInfo}
                      initialValues={initialValues}
                      shellUser={shellUser}
                      invitedBy={{
                        invitee,
                        invitationReason,
                      }}
                    />
                  </div>
                </Route>
                <Route path={INFO_STEP}>
                  <div className="content">
                    <Info className="content" userType={userType} userInfo={userInfo} handleContinue={goToWizard} />
                  </div>
                </Route>
                <Route path={ACCOUNT_SETUP}>
                  <SignupWizard userType={userType} userInfo={userInfo} initialValues={initialValues} />
                </Route>
              </Switch>
                  )}
          </React.Fragment>
            )}
      </div>
    </div>
  )
}

export default NewSignup
